import React, { useEffect, useState } from 'react'
import { Checkbox, Input, Button } from 'antd'
import { DoubleLeftOutlined, DoubleRightOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import styled from 'styled-components'

const TransferContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
`

const TransferBox = styled.div`
  width: 50%;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: white;
`

const TransferHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
`

const HeaderInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  color: #666;
  font-size: 14px;
  font-weight: 600;
`

const SearchInput = styled(Input)`
  width: 100%;
  border-radius: 4px;
  
  &.ant-input {
    padding: 8px;
    font-size: 14px;
  }
`

const TransferBody = styled.div`
  height: 400px;
  overflow-y: auto;
`

const TransferList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
`

const TransferItem = styled.div`
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }

  .ant-checkbox-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    
    .ant-checkbox {
      margin: 0;
      padding: 12px;
      border-right: 1px solid #f0f0f0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    span:last-child {
      font-weight: 500;
      flex: 1;
    }
  }
`

const TransferControls = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

const TransferButton = styled(Button)`
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #E6E7EA;
  border: none;

  &:hover, &:focus {
    background-color: #E6E7EA;
  }

  &[disabled] {
    background-color: #E6E7EA;
  }
`

const TransferWithCheckbox = ({ data, onChange }) => {
  const [sourceItems, setSourceItems] = useState([])
  const [targetItems, setTargetItems] = useState([])
  const [sourceSearch, setSourceSearch] = useState('')
  const [targetSearch, setTargetSearch] = useState('')
  const [selectedSourceKeys, setSelectedSourceKeys] = useState([])
  const [selectedTargetKeys, setSelectedTargetKeys] = useState([])

  const filteredSourceItems = sourceItems.filter(item => 
    item.title.toLowerCase().includes(sourceSearch?.toLowerCase()?.trim())
  )

  const filteredTargetItems = targetItems.filter(item =>
    item.title.toLowerCase().includes(targetSearch?.toLowerCase()?.trim())
  )

  const handleMoveAllRight = () => {
    setTargetItems([...targetItems, ...sourceItems])
    setSourceItems([])
    onChange?.([...targetItems, ...sourceItems].map(item => item.key))
  }

  const handleMoveAllLeft = () => {
    setSourceItems([...sourceItems, ...targetItems])
    setTargetItems([])
    onChange?.([])
  }

  const moveToTarget = () => {
    const itemsToMove = sourceItems.filter(item => selectedSourceKeys.includes(item.key))
    setTargetItems([...targetItems, ...itemsToMove])
    setSourceItems(sourceItems.filter(item => !selectedSourceKeys.includes(item.key)))
    setSelectedSourceKeys([])
    onChange?.([...targetItems, ...itemsToMove].map(item => item.key))
  }

  const moveToSource = () => {
    const itemsToMove = targetItems.filter(item => selectedTargetKeys.includes(item.key))
    setSourceItems([...sourceItems, ...itemsToMove])
    setTargetItems(targetItems.filter(item => !selectedTargetKeys.includes(item.key)))
    setSelectedTargetKeys([])
    onChange?.(targetItems.filter(item => !selectedTargetKeys.includes(item.key)).map(item => item.key))
  }

  const handleSelectAllSource = (e) => {
    if (e.target.checked) {
      setSelectedSourceKeys(filteredSourceItems.map(item => item.key))
    } else {
      setSelectedSourceKeys([])
    }
  }

  const handleSelectAllTarget = (e) => {
    if (e.target.checked) {
      setSelectedTargetKeys(filteredTargetItems.map(item => item.key))
    } else {
      setSelectedTargetKeys([])
    }
  }

  useEffect(() => {
    if(data?.length){
      setSourceItems(data)
    }
  },[data])

  return (
    <TransferContainer>
      <TransferBox>
        <TransferHeader>
          <HeaderInfo>
            <span>{filteredSourceItems.length} Items</span>
          </HeaderInfo>
          <SearchInput 
            placeholder="Search"
            value={sourceSearch}
            onChange={e => setSourceSearch(e.target.value)}
          />
        </TransferHeader>
        <TransferBody>
          <TransferList>
            <TransferItem>
              <Checkbox 
                checked={selectedSourceKeys.length === filteredSourceItems.length && filteredSourceItems.length > 0}
                indeterminate={selectedSourceKeys.length > 0 && selectedSourceKeys.length < filteredSourceItems.length}
                onChange={handleSelectAllSource}
              >
                Select all
              </Checkbox>
            </TransferItem>
            {filteredSourceItems.map(item => (
              <TransferItem key={item.key}>
                <Checkbox
                  checked={selectedSourceKeys.includes(item.key)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedSourceKeys([...selectedSourceKeys, item.key])
                    } else {
                      setSelectedSourceKeys(selectedSourceKeys.filter(key => key !== item.key))
                    }
                  }}
                >
                  {item.title}
                </Checkbox>
              </TransferItem>
            ))}
          </TransferList>
        </TransferBody>
      </TransferBox>

      <TransferControls>
        <TransferButton
          onClick={handleMoveAllRight}
          disabled={sourceItems.length === 0}
        >
          <DoubleRightOutlined
            style={{ color: '#999FAC', fontSize: '18px' }}
          />
        </TransferButton>

        <TransferButton 
          onClick={moveToTarget} 
          disabled={selectedSourceKeys.length === 0}
        >
          <RightOutlined style={{ color: '#999FAC', fontSize: '18px' }} />
        </TransferButton>

        <TransferButton 
          onClick={moveToSource} 
          disabled={selectedTargetKeys.length === 0}
        >
          <LeftOutlined style={{ color: '#999FAC', fontSize: '18px' }} />
        </TransferButton>

        <TransferButton
          onClick={handleMoveAllLeft}
          disabled={targetItems.length === 0}
        >
          <DoubleLeftOutlined
            style={{ color: '#999FAC', fontSize: '18px' }}
          />
        </TransferButton>
      </TransferControls>

      <TransferBox>
        <TransferHeader>
          <HeaderInfo>
            <span>{filteredTargetItems.length} Items</span>
          </HeaderInfo>
          <SearchInput 
            placeholder="Search"
            value={targetSearch}
            onChange={e => setTargetSearch(e.target.value)}
          />
        </TransferHeader>
        <TransferBody>
          <TransferList>
            <TransferItem>
              <Checkbox 
                checked={selectedTargetKeys.length === filteredTargetItems.length && filteredTargetItems.length > 0}
                indeterminate={selectedTargetKeys.length > 0 && selectedTargetKeys.length < filteredTargetItems.length}
                onChange={handleSelectAllTarget}
              >
                Select all
              </Checkbox>
            </TransferItem>
            {filteredTargetItems.map(item => (
              <TransferItem key={item.key}>
                <Checkbox
                  checked={selectedTargetKeys.includes(item.key)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedTargetKeys([...selectedTargetKeys, item.key])
                    } else {
                      setSelectedTargetKeys(selectedTargetKeys.filter(key => key !== item.key))
                    }
                  }}
                >
                  {item.title}
                </Checkbox>
              </TransferItem>
            ))}
          </TransferList>
        </TransferBody>
      </TransferBox>
    </TransferContainer>
  )
}

export default TransferWithCheckbox
